import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { creditosFinanceiros, CreditoFinanceiro } from '@/mocks/creditosFinanceiros';
import { calcularPontosFinanceiro } from '@/utils/pontosFinanceiro';

interface PixPagamentoCardProps {
  onResgatar: (tipo: 'pix' | 'pagamento', creditoId: string) => void;
  dataTourCard?: string;
  dataTourBtn?: string;
}

type ModalidadeTipo = 'pix' | 'pagamento';

const MODALIDADE_CONFIG: Record<ModalidadeTipo, {
  label: string;
  icone: string;
  tagBase: string;
  acaoLabel: (valor: number) => string;
  descricao: string;
}> = {
  pix: {
    label: 'PIX',
    icone: 'ri-bank-card-line',
    tagBase: 'PIX Personalizado',
    acaoLabel: (valor) => `Receber R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} via PIX`,
    descricao: 'Resgate via PIX com valor personalizado',
  },
  pagamento: {
    label: 'Pagamento de Contas',
    icone: 'ri-file-list-3-line',
    tagBase: 'Pagamento Personalizado',
    acaoLabel: (valor) => `Pagar conta de até R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
    descricao: 'Pagamento de contas com valor personalizado',
  },
};

function getDiasParaExpirar(credito: CreditoFinanceiro): number {
  const dataCredito = new Date(credito.dataCredito);
  const dataExpiracao = new Date(dataCredito);
  dataExpiracao.setDate(dataExpiracao.getDate() + Math.min(credito.diasExpiracao, 360));
  const hoje = new Date();
  return Math.ceil((dataExpiracao.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
}

function getDataExpiracao(credito: CreditoFinanceiro): Date {
  const dataCredito = new Date(credito.dataCredito);
  const dataExpiracao = new Date(dataCredito);
  dataExpiracao.setDate(dataExpiracao.getDate() + Math.min(credito.diasExpiracao, 360));
  return dataExpiracao;
}

export default function PixPagamentoCard({ onResgatar, dataTourCard, dataTourBtn }: PixPagamentoCardProps) {
  const navigate = useNavigate();
  const [modalidade, setModalidade] = useState<ModalidadeTipo>('pix');
  const [creditoSelecionadoId, setCreditoSelecionadoId] = useState<string>(
    creditosFinanceiros.length > 0 ? creditosFinanceiros[0].id : ''
  );

  const config = MODALIDADE_CONFIG[modalidade];
  const creditoSelecionado = creditosFinanceiros.find(c => c.id === creditoSelecionadoId) ?? null;

  const diasParaExpirar = creditoSelecionado ? getDiasParaExpirar(creditoSelecionado) : null;
  const dataExpiracao = creditoSelecionado ? getDataExpiracao(creditoSelecionado) : null;
  const dataCredito = creditoSelecionado ? new Date(creditoSelecionado.dataCredito) : null;
  const expirado = diasParaExpirar !== null && diasParaExpirar <= 0;
  const urgente = diasParaExpirar !== null && diasParaExpirar > 0 && diasParaExpirar <= 60;
  const muitoUrgente = diasParaExpirar !== null && diasParaExpirar > 0 && diasParaExpirar <= 30;
  const pontos = creditoSelecionado ? calcularPontosFinanceiro(creditoSelecionado.valor) : 0;

  const borderClass = muitoUrgente
    ? 'border-red-400 ring-2 ring-red-400'
    : urgente
    ? 'border-amber-400 ring-2 ring-amber-400'
    : 'border-slate-200';

  const imagem = modalidade === 'pix'
    ? 'https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/d0bca9b579fa5bbed4a08ef0669cd18f.png'
    : 'https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/32c6fb1e1592b974c4b053fbb384b0c1.png';

  return (
    <div
      data-tour={dataTourCard}
      className={`bg-white rounded-xl overflow-hidden border hover:shadow-xl transition-all hover:-translate-y-1 group ${borderClass}`}
    >
      {/* Imagem — troca conforme modalidade */}
      <div className="relative h-48 overflow-hidden bg-slate-100">
        <img
          key={imagem}
          src={imagem}
          alt={config.label}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
        />
      </div>

      {/* Conteúdo */}
      <div className="p-5 space-y-3">

        {/* Radio buttons — escolha da modalidade */}
        <div style={{ display: 'flex', gap: 10 }}>
          {(Object.keys(MODALIDADE_CONFIG) as ModalidadeTipo[]).map((tipo) => {
            const ativo = modalidade === tipo;
            return (
              <label
                key={tipo}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '12px 16px',
                  borderRadius: 12,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.18s',
                  border: ativo ? '2px solid #f97316' : '2px solid #e2e8f0',
                  background: ativo ? 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)' : 'white',
                  fontWeight: 700,
                  fontSize: 13,
                  color: ativo ? '#ea580c' : '#94a3b8',
                }}
              >
                <input
                  type="radio"
                  name="modalidade-financeira"
                  value={tipo}
                  checked={ativo}
                  onChange={() => setModalidade(tipo)}
                  style={{ display: 'none' }}
                />
                <span style={{
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  background: ativo ? '#f97316' : '#f1f5f9',
                  flexShrink: 0,
                  transition: 'all 0.18s',
                }}>
                  <i className={MODALIDADE_CONFIG[tipo].icone} style={{ color: ativo ? 'white' : '#94a3b8', fontSize: 14 }}></i>
                </span>
                {MODALIDADE_CONFIG[tipo].label}
              </label>
            );
          })}
        </div>

        {/* Título e descrição */}
        <div>
          <h3 className="text-lg font-bold text-slate-900">
            {creditoSelecionado
              ? `${config.label} — ${creditoSelecionado.descricao}`
              : config.label}
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            {creditoSelecionado
              ? `Campanha: ${creditoSelecionado.campanha} • ${config.descricao}`
              : config.descricao}
          </p>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
            {config.tagBase}
          </span>
          {creditoSelecionado && (
            <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
              {creditoSelecionado.campanha}
            </span>
          )}
        </div>

        {/* Seletor de crédito */}
        {creditosFinanceiros.length > 0 && (
          <div style={{
            borderRadius: 14,
            border: '2px solid #f97316',
            background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
            padding: '14px 16px',
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              fontWeight: 800,
              color: '#9a3412',
              marginBottom: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
            }}>
              <span style={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f97316',
                borderRadius: '50%',
                flexShrink: 0,
              }}>
                <i className="ri-list-check" style={{ color: 'white', fontSize: 12 }}></i>
              </span>
              Selecione o bonus para resgatar
            </label>
            <div style={{ position: 'relative' }}>
              <select
                value={creditoSelecionadoId}
                onChange={e => setCreditoSelecionadoId(e.target.value)}
                style={{
                  width: '100%',
                  paddingLeft: 14,
                  paddingRight: 44,
                  paddingTop: 10,
                  paddingBottom: 10,
                  border: '2px solid #f97316',
                  borderRadius: 10,
                  fontSize: 13,
                  color: '#1e293b',
                  background: 'white',
                  cursor: 'pointer',
                  fontWeight: 600,
                  appearance: 'none',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              >
                {creditosFinanceiros.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.descricao} — R$ {c.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ({c.campanha})
                  </option>
                ))}
              </select>
              <div style={{
                pointerEvents: 'none',
                position: 'absolute',
                top: '50%',
                right: 10,
                transform: 'translateY(-50%)',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f97316',
                borderRadius: '50%',
              }}>
                <i className="ri-arrow-down-s-line" style={{ color: 'white', fontSize: 16 }}></i>
              </div>
            </div>
          </div>
        )}

        {/* Ação e pontos */}
        {creditoSelecionado && (
          <div className="pt-1">
            <p className="text-sm text-slate-700 font-medium">
              {config.acaoLabel(creditoSelecionado.valor)}
            </p>
            <p className="text-3xl font-bold text-orange-600 mt-0.5">
              {pontos.toLocaleString('pt-BR')}
              <span className="text-base text-slate-500 ml-1 font-normal">pts</span>
            </p>
          </div>
        )}

        {/* Data de crédito e expiração */}
        {creditoSelecionado && dataCredito && dataExpiracao && (
          <div className="pt-2 border-t border-slate-100 space-y-2">
            {/* Creditado em */}
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-slate-500"></i>
              </div>
              <span>
                Creditado em{' '}
                <strong className="text-slate-800">{dataCredito.toLocaleDateString('pt-BR')}</strong>
              </span>
              <button
                onClick={() =>
                  navigate(
                    `/premiado/extrato?busca=${encodeURIComponent(dataCredito.toLocaleDateString('pt-BR'))}&tipo=bonus`
                  )
                }
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-50 text-teal-700 text-sm font-semibold hover:bg-teal-100 transition-colors cursor-pointer whitespace-nowrap border border-teal-200"
              >
                <i className="ri-external-link-line"></i>
                Ver no histórico
              </button>
            </div>

            {/* Expiração */}
            <div
              className={`flex items-center gap-2 text-sm font-medium ${
                expirado || muitoUrgente ? 'text-red-600' : urgente ? 'text-amber-600' : 'text-slate-600'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${
                  expirado || muitoUrgente
                    ? 'ri-time-line text-red-500'
                    : urgente
                    ? 'ri-alarm-warning-line text-amber-500'
                    : 'ri-timer-line text-slate-500'
                }`}></i>
              </div>
              {expirado ? (
                <span className="text-red-600 font-semibold">Crédito expirado</span>
              ) : (
                <span>
                  Expira em{' '}
                  <strong>{diasParaExpirar} dia{diasParaExpirar !== 1 ? 's' : ''}</strong>{' '}
                  ({dataExpiracao.toLocaleDateString('pt-BR')})
                </span>
              )}
            </div>

            {/* Badge de alerta */}
            {urgente && !expirado && (
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${
                  muitoUrgente
                    ? 'bg-red-100 text-red-700 border border-red-200 animate-pulse'
                    : 'bg-amber-100 text-amber-700 border border-amber-200'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={muitoUrgente ? 'ri-alarm-warning-fill' : 'ri-error-warning-fill'}></i>
                </div>
                <span>
                  {muitoUrgente
                    ? '⚠️ Atenção! Menos de 30 dias para expirar'
                    : '⏰ Expira em menos de 60 dias - Resgate já!'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Botão Resgatar */}
        <button
          data-tour={dataTourBtn}
          onClick={() => creditoSelecionadoId && onResgatar(modalidade, creditoSelecionadoId)}
          disabled={expirado || !creditoSelecionadoId}
          className={`w-full py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
            !expirado && creditoSelecionadoId
              ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:shadow-lg hover:from-orange-600 hover:to-orange-700'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          {expirado ? (
            <>
              <i className="ri-lock-line"></i>
              Crédito Expirado
            </>
          ) : (
            <>
              <i className="ri-gift-line"></i>
              Resgatar Agora
            </>
          )}
        </button>
      </div>
    </div>
  );
}
